// Pure calendar-workbook shaping. This module intentionally has no browser or
// API dependencies so the same multi-event behavior can be regression-tested
// with plain Node.

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]
const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']

const isoDate = d =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const cleanClientName = clientName => {
  const name = String(clientName || '')
  const match = name.match(/^3PA - (.+?) - Agency .+$/)
  return match ? match[1] : name
}

const calendarDays = (dateFrom, dateTo) => {
  const start = new Date(dateFrom + 'T00:00:00')
  const end = new Date(dateTo + 'T00:00:00')
  const days = []
  // Advance by local calendar date instead of elapsed milliseconds. A day at
  // the spring DST transition is only 23 hours; millisecond division silently
  // omitted the final date from March exports in Pacific time.
  let current = new Date(start)
  while (current <= end) {
    days.push({ dateStr: isoDate(current) })
    const next = new Date(current)
    next.setDate(next.getDate() + 1)
    current = next
  }
  return days
}

const calendarDesignators = events => {
  const seen = new Set()
  const clientWOs = {}
  for (const date of Object.keys(events).sort()) {
    for (const event of events[date]) {
      const wo = event.work_order
      const name = cleanClientName(event.client_name)
      if (wo && !seen.has(wo)) {
        seen.add(wo)
        if (!clientWOs[name]) clientWOs[name] = []
        clientWOs[name].push(wo)
      }
    }
  }
  const map = {}
  for (const wos of Object.values(clientWOs)) {
    if (wos.length > 1) wos.forEach((wo, i) => { map[wo] = ` -${i + 1}` })
  }
  return map
}

const shapeCalendar = (stores, events, dateFrom, dateTo) => {
  const days = calendarDays(dateFrom, dateTo)
  const designators = calendarDesignators(events)

  const eventMap = {}
  for (const [date, dayEvents] of Object.entries(events)) {
    const seenScheduleIds = new Set()
    for (const event of dayEvents) {
      if (event.schedule_id != null && event.schedule_id !== '') {
        const scheduleKey = String(event.schedule_id)
        if (seenScheduleIds.has(scheduleKey)) continue
        seenScheduleIds.add(scheduleKey)
      }
      if (!eventMap[event.costco_id]) eventMap[event.costco_id] = {}
      if (!eventMap[event.costco_id][date]) eventMap[event.costco_id][date] = []
      eventMap[event.costco_id][date].push(event)
    }
  }

  const nameByStore = {}
  for (const date of Object.keys(events)) {
    for (const event of events[date]) {
      nameByStore[event.costco_id] = event.storename
    }
  }

  const chipLabel = event =>
    cleanClientName(event.client_name) + (designators[event.work_order] || '')

  const startDate = new Date(dateFrom + 'T00:00:00')
  const title = `${MONTHS[startDate.getMonth()]} ${startDate.getFullYear()}`

  const dayDates = days.map(day => new Date(day.dateStr + 'T00:00:00'))
  const dowRow = dayDates.map(d => WEEKDAYS[d.getDay()])
  const dateRow = dayDates.map(d => `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`)
  const timeRow = dayDates.map(d => (d.getDay() === 0 || d.getDay() === 6) ? '12-4 PM' : '2-6 PM')

  const header1 = [title, '', '', ...dowRow]
  const header2 = ['', '', '', ...dateRow]
  const header3 = ['CLUSTER', 'STORE #', 'CITY', ...timeRow]

  const dataRows = []
  for (const store of stores) {
    const name = nameByStore[store.costco_id]
    const eventsByDay = days.map(day =>
      ((eventMap[store.costco_id] || {})[day.dateStr] || [])
        .map(e => chipLabel(e) + (e.boosted ? ' (Boosted)' : ''))
    )
    const rowCount = Math.max(1, ...eventsByDay.map(dayEvents => dayEvents.length))

    // The community SheetJS writer cannot persist wrapText styles. Emit a real
    // worksheet row per event slot so multiple events are visibly separate in
    // Excel instead of relying on invisible in-cell newline characters.
    for (let eventIndex = 0; eventIndex < rowCount; eventIndex += 1) {
      dataRows.push([
        store.cluster || '',
        store.warehouse_id,
        name ? `${name} - ${store.city}` : store.city,
        ...eventsByDay.map(dayEvents => dayEvents[eventIndex] || '')
      ])
    }
  }

  const aoa = [header1, header2, header3, ...dataRows]
  const merges = [{ s: { r: 0, c: 0 }, e: { r: 1, c: 2 } }]
  const cols = [{ wch: 10 }, { wch: 8 }, { wch: 64 }, ...days.map(() => ({ wch: 24 }))]
  return { name: 'Calendar', aoa, merges, cols }
}

export { shapeCalendar }
